import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Inbox, Mail, Phone, Building2, Truck, Hash, MessageSquare, CheckCircle2, X as XIcon } from "lucide-react";

const STATUS_TAG = {
  new:       "hs-tag-info",
  contacted: "hs-tag-warn",
  qualified: "hs-tag-warn",
  converted: "hs-tag-success",
  dismissed: "hs-tag-danger",
  duplicate: "hs-tag-info",
};

export default function AdminLeads() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [data, setData] = useState({ leads: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = filter ? `/admin/leads?status=${filter}` : "/admin/leads";
      const { data } = await api.get(url);
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const setStatus = async (lead, status) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/leads/${lead.id}`, { status, note: note || undefined });
      toast.success(`Marked ${status.toUpperCase()}`);
      setNote("");
      setActive(data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Lead deleted");
      setActive(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-leads-page">
      <div>
        <div className="hs-overline text-blue-700">SALES PIPELINE</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Leads Inbox</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-3xl">
          Every prospect who submits the &ldquo;Get a Demo&rdquo; form on the marketing site lands here.
          Contact them, qualify them, and once they sign on, manually create their client account in the Clients tab.
        </p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="admin-leads-stats">
        {["total", "new", "contacted", "qualified", "converted", "dismissed", "duplicate"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k === "total" ? "" : k)}
            className={`hs-card p-3 text-left transition ${(filter === "" && k === "total") || filter === k ? "ring-2 ring-blue-700" : ""}`}
            data-testid={`leads-filter-${k}`}
          >
            <div className="hs-overline text-[10px] text-slate-500">{k.toUpperCase()}</div>
            <div className={`font-display font-black text-3xl font-mono mt-1 ${
              k === "converted" ? "text-emerald-600" :
              k === "new"       ? "text-blue-700" :
              k === "dismissed" ? "text-rose-600" : ""
            }`}>{data.counts?.[k] ?? 0}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Leads list */}
        <div className="lg:col-span-2 hs-card p-0 overflow-hidden" data-testid="admin-leads-list">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2">
              <Inbox size={14} className="text-blue-700" />
              <h2 className="font-display font-bold text-sm uppercase tracking-tight">
                {filter || "All"} ({data.leads?.length || 0})
              </h2>
            </div>
            {filter && (
              <button onClick={() => setFilter("")} className="text-xs text-slate-500 hover:text-slate-900">
                Clear filter
              </button>
            )}
          </div>
          <div className="max-h-[700px] overflow-y-auto">
            {loading ? (
              <div className="p-6 hs-overline">LOADING…</div>
            ) : (data.leads || []).length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                <Inbox size={28} className="mx-auto text-slate-300" />
                <p className="mt-3">No leads yet.</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  The form at <span className="font-mono">/register</span> sends submissions here.
                </p>
              </div>
            ) : (
              data.leads.map((l) => (
                <button
                  key={l.id}
                  onClick={() => { setActive(l); setNote(""); }}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition ${
                    active?.id === l.id ? "bg-blue-50" : ""
                  }`}
                  data-testid={`lead-row-${l.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{l.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{l.company_name || l.email}</div>
                    </div>
                    <span className={`hs-tag ${STATUS_TAG[l.status] || "hs-tag-info"} text-[9px]`}>
                      {(l.status || "new").toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-slate-500 font-mono mt-1">
                    <span>{(l.created_at || "").slice(0, 10)}</span>
                    {l.number_of_trucks != null && <span>· {l.number_of_trucks} trucks</span>}
                    {l.mc_number && <span>· MC {l.mc_number}</span>}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Detail pane */}
        <div className="lg:col-span-3 hs-card p-6" data-testid="admin-leads-detail">
          {!active ? (
            <div className="text-center py-16 text-slate-500">
              <MessageSquare size={32} className="mx-auto text-slate-300" />
              <p className="mt-3 text-sm">Select a lead to view details.</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="hs-overline text-[10px] text-slate-500">LEAD</div>
                  <h2 className="font-display font-bold text-2xl mt-1">{active.name}</h2>
                  <div className="text-sm text-slate-600">{active.company_name || "—"}</div>
                </div>
                <span className={`hs-tag ${STATUS_TAG[active.status] || "hs-tag-info"}`}>
                  {(active.status || "new").toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div className="flex items-center gap-2"><Mail size={12} className="text-blue-700" />
                  <a href={`mailto:${active.email}`} className="text-blue-700 hover:underline">{active.email}</a>
                </div>
                <div className="flex items-center gap-2"><Phone size={12} className="text-blue-700" />
                  <a href={`tel:${active.phone}`} className="text-blue-700 hover:underline font-mono">{active.phone}</a>
                </div>
                <div className="flex items-center gap-2"><Hash size={12} className="text-slate-400" />
                  <span className="text-slate-700 font-mono">MC: {active.mc_number || "—"}</span>
                </div>
                <div className="flex items-center gap-2"><Hash size={12} className="text-slate-400" />
                  <span className="text-slate-700 font-mono">DOT: {active.dot_number || "—"}</span>
                </div>
                <div className="flex items-center gap-2"><Truck size={12} className="text-slate-400" />
                  <span className="text-slate-700">Trucks: <span className="font-mono">{active.number_of_trucks ?? "—"}</span></span>
                </div>
                <div className="flex items-center gap-2"><Building2 size={12} className="text-slate-400" />
                  <span className="text-slate-700">{active.fleet_type || "—"}</span>
                </div>
              </div>

              {active.message && (
                <div className="bg-slate-50 border-l-2 border-blue-700 p-3 text-sm">
                  <div className="hs-overline text-[10px] text-slate-500 mb-1">MESSAGE</div>
                  {active.message}
                </div>
              )}

              {(active.notes || []).length > 0 && (
                <div>
                  <div className="hs-overline text-[10px] text-slate-500 mb-2">INTERNAL NOTES</div>
                  <ul className="space-y-2">
                    {active.notes.map((n, i) => (
                      <li key={i} className="text-xs border-l-2 border-slate-200 pl-3">
                        <span className="font-medium">{n.actor}</span>{" "}
                        <span className="text-slate-400 font-mono">— {(n.at || "").slice(0, 16).replace("T", " ")}</span>
                        <div className="text-slate-700">{n.text}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <label className="hs-overline mb-1 block">Add note (optional, attached to next status change)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)}
                          className="hs-input min-h-[60px] text-sm"
                          placeholder="Called — discussed needs. Following up Thursday."
                          data-testid="lead-note-input" />
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-200">
                <button onClick={() => setStatus(active, "contacted")} disabled={busy}
                        className="hs-btn-secondary text-xs inline-flex items-center gap-1"
                        data-testid="lead-mark-contacted">
                  <CheckCircle2 size={12} /> Mark Contacted
                </button>
                <button onClick={() => setStatus(active, "qualified")} disabled={busy}
                        className="hs-btn-secondary text-xs"
                        data-testid="lead-mark-qualified">Qualified</button>
                <button onClick={() => setStatus(active, "converted")} disabled={busy}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wide rounded inline-flex items-center gap-1"
                        data-testid="lead-mark-converted">
                  <CheckCircle2 size={12} /> Converted
                </button>
                <button onClick={() => setStatus(active, "dismissed")} disabled={busy}
                        className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-wide rounded inline-flex items-center gap-1"
                        data-testid="lead-mark-dismissed">
                  <XIcon size={12} /> Dismiss
                </button>
                {isSuperAdmin && (
                  <button onClick={() => handleDelete(active)} className="ml-auto text-[11px] text-slate-400 hover:text-rose-600"
                          data-testid="lead-delete-btn">
                    Delete permanently
                  </button>
                )}
              </div>

              <p className="text-[11px] text-slate-400">
                Once you mark this lead <strong>Converted</strong>, go to <span className="font-mono">Clients → Add Client</span>{" "}
                and create their account with the info above. Their CSA snapshot auto-pulls within 10 seconds.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
